// Cannonballs in flight, plus the muzzle discharge and impact either end.
//
// Everything here is matched to images/canon_projectile.mov frame by frame.
// The measurements taken off that footage, which drove all the constants below:
//
//   ball diameter     0.45x the cannon's width
//   trajectory        flat — the ball's screen y does not change in flight
//   muzzle fireball   ~130ms, mean rgb(235, 200, 135)
//   muzzle smoke      ~500ms, mean rgb(217, 208, 178), grows to ~1.4x the
//                     cannon's width and climbs about its own height
//   flight trail      discrete puffs left along the path, rgb(208, 199, 164),
//                     still visible several frames after the ball has passed
//   ball speed        ~11.5 cannon-widths per second
//
// A shot is fully described by (from, to, launch, flight_ms), so all of this is
// a pure function of what the server broadcast once. Every client computes the
// identical animation at frame rate; the server never streams ball positions.
import { UNIT_DRAW_SIZE } from "../../constants/GameConstants";

// Nearly flat, as in the reference. Not perfectly flat: a few pixels of loft is
// what separates the ball from its own shadow, and without that gap a dark disc
// sliding over grass reads as a ground decal rather than as something in the
// air.
const ARC_RATIO = 0.07;
const MAX_PEAK = UNIT_DRAW_SIZE * 0.3;
// 0.45 of the cannon's width, and the cannon is a bit over half the cell.
const BALL_RADIUS = UNIT_DRAW_SIZE * 0.115;

export const MUZZLE_MS = 620;
export const TRAIL_MS = 640;
export const BLAST_MS = 700;
const FIREBALL_MS = 140;
// In the reference the first two frames are clean fire, and smoke only shows
// from the third. Starting both at once washed the orange out to a smudge.
const SMOKE_DELAY_MS = 95;

// Smoke is not one colour. In the reference the underside and left of each
// lobe sit in shadow at around rgb(150, 142, 120) while the crowns catch the
// light at nearly white, and it is that spread — not the mean — that makes the
// cloud read as a solid volume rather than a flat cream smear.
const SMOKE_DARK = [150, 143, 122];
const SMOKE = [217, 208, 178];
const SMOKE_LIT = [248, 246, 238];
const SMOKE_OLD = [238, 236, 226];

const lerp = (a, b, t) => a + (b - a) * t;

// Deterministic per-shot variation. Clouds have to differ from each other but
// be identical frame to frame — anything re-rolled per frame makes the smoke
// seethe instead of billow.
const hash = (seed, index) => {
  let h = 2166136261;
  const text = `${seed}:${index}`;
  for (let i = 0; i < text.length; i++) {
    h = Math.imul(h ^ text.charCodeAt(i), 16777619);
  }
  return ((h >>> 0) % 10000) / 10000;
};

// The lobes of one billowing cloud. Each rises at its own rate and grows on its
// own curve, which is what gives the cauliflower silhouette rather than a disc.
const LOBES = [
  { x: 0.0, y: 0.05, r: 0.54, rise: 0.55, delay: 0.0 },
  { x: -0.34, y: 0.12, r: 0.42, rise: 0.42, delay: 0.04 },
  { x: 0.33, y: 0.14, r: 0.4, rise: 0.4, delay: 0.05 },
  { x: 0.06, y: -0.1, r: 0.44, rise: 0.62, delay: 0.08 },
  { x: -0.14, y: -0.34, r: 0.46, rise: 0.78, delay: 0.12 },
  { x: 0.2, y: -0.42, r: 0.4, rise: 0.85, delay: 0.18 },
  { x: -0.02, y: -0.52, r: 0.38, rise: 0.92, delay: 0.22 },
  { x: -0.04, y: -0.74, r: 0.38, rise: 1.05, delay: 0.28 },
  { x: 0.26, y: -0.9, r: 0.3, rise: 1.2, delay: 0.4 },
  { x: -0.3, y: -0.86, r: 0.28, rise: 1.15, delay: 0.44 },
  { x: 0.02, y: -1.05, r: 0.26, rise: 1.32, delay: 0.52 },
];

/**
 * One smoke cloud, unfurling away from (x, y).
 *
 * The lobes are laid out around a local axis that runs straight up, so by
 * default the cloud climbs. `axis` rotates that: pass the direction the smoke
 * should travel and the whole plume tips over to follow it, which is what a
 * barrel venting sideways does — it throws smoke out along its own line, not
 * into the air. `heightScale` shortens the cloud along that same axis without
 * narrowing it.
 */
const drawBillow = (ctx, x, y, t, size, seed, heightScale = 1, axis = null) => {
  if (t < 0 || t > 1) {
    return;
  }
  ctx.save();
  // Squash about the base rather than scaling each lobe's offset. Halving the
  // offsets alone only compressed the gaps between puffs — every puff kept its
  // own diameter, so a "half height" cloud came out barely a fifth shorter. A
  // transform takes the radii with it.
  //
  // Rotation has to wrap the scale, not follow it, so the squash stays along
  // the plume's own axis instead of always flattening it against the screen.
  if (axis !== null || heightScale !== 1) {
    ctx.translate(x, y);
    if (axis !== null) {
      // The lobes' local "up" is -y, which points at -90 degrees; turn that to
      // face `axis`.
      ctx.rotate(axis + Math.PI / 2);
    }
    ctx.scale(1, heightScale);
    ctx.translate(-x, -y);
  }
  for (let i = 0; i < LOBES.length; i++) {
    const lobe = LOBES[i];
    // Each lobe starts at its own moment, so the cloud unfurls upward over its
    // life instead of appearing whole and merely scaling.
    const local = (t - lobe.delay) / (1 - lobe.delay);
    if (local <= 0) {
      continue;
    }
    const grown = 1 - Math.pow(1 - local, 2.2);
    const jitterX = (hash(seed, i) - 0.5) * 0.26;
    const jitterR = 0.82 + hash(seed, i + 40) * 0.42;
    const radius = size * lobe.r * jitterR * (0.35 + 0.65 * grown);
    const cx = x + size * (lobe.x + jitterX) * (0.5 + 0.5 * grown);
    const cy = y + size * lobe.y * grown - size * lobe.rise * grown;

    // Smoke pales as it thins, so an old cloud is near-white rather than just a
    // faded copy of the colour it started at.
    const age = Math.min(1, local * 1.15);
    // Lobes high in the column catch the light; the ones banked low and left
    // sit in the cloud's own shadow.
    const lit = Math.max(0, Math.min(1, 0.42 - lobe.y * 0.42 + lobe.x * 0.18));
    const body = lit < 0.5
      ? SMOKE_DARK.map((c, k) => lerp(c, SMOKE[k], lit * 2))
      : SMOKE.map((c, k) => lerp(c, SMOKE_LIT[k], (lit - 0.5) * 2));
    const tint = body.map((c, k) => Math.round(lerp(c, SMOKE_OLD[k], age)));
    // and each lobe is lit from its own upper right, so it has a bright crown
    // and a shaded belly instead of being one even tone
    const shade = SMOKE_DARK.map((c, k) => Math.round(lerp(c, tint[k], 0.55 + 0.45 * age)));
    // Held near-opaque for most of the cloud's life and then dropped fast.
    // A gentle linear fade measured out at a thirtieth of the reference's
    // smoke density: the pixels were all there, just too transparent to read
    // as smoke against grass at all.
    const alpha = 1.0 * (1 - Math.pow(t, 2.4)) * Math.min(1, local * 5);

    // Each lobe is a radial gradient, not a flat disc. Flat fills left every
    // lobe's outline visible, so the cloud read as a stack of overlapping
    // circles; fading each one out at its rim lets them melt together into a
    // single soft mass the way the reference smoke does.
    const rgb = `${tint[0]}, ${tint[1]}, ${tint[2]}`;
    const dim = `${shade[0]}, ${shade[1]}, ${shade[2]}`;
    const grad = ctx.createRadialGradient(
      cx + radius * 0.3, cy - radius * 0.34, radius * 0.08,
      cx, cy, radius);
    grad.addColorStop(0, `rgba(${rgb}, ${alpha})`);
    grad.addColorStop(0.42, `rgba(${rgb}, ${alpha * 0.9})`);
    grad.addColorStop(0.72, `rgba(${dim}, ${alpha * 0.6})`);
    grad.addColorStop(1, `rgba(${dim}, 0)`);
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.ellipse(cx, cy, radius, radius * 0.88, 0, 0, 2 * Math.PI);
    ctx.fill();
  }
  ctx.restore();
};

/** Where the ball is at time t, and how far off the ground. */
const ballAt = (shot, t) => {
  const x = lerp(shot.from.x, shot.to.x, t);
  const groundY = lerp(shot.from.y, shot.to.y, t);
  const span = Math.hypot(shot.to.x - shot.from.x, shot.to.y - shot.from.y);
  const peak = Math.min(MAX_PEAK, span * ARC_RATIO);
  return { x, groundY, height: 4 * peak * t * (1 - t), peak };
};

// Puffs are dropped at fixed points along the path and left behind, which is
// how the reference reads: a dotted line of smoke marking where the ball went,
// outliving the ball itself by a good half second.
const TRAIL_PUFFS = 9;

export const drawTrail = (ctx, shot, elapsedMs) => {
  ctx.save();
  for (let i = 1; i <= TRAIL_PUFFS; i++) {
    const at = i / (TRAIL_PUFFS + 1);
    const age = (elapsedMs - at * shot.flight_ms) / TRAIL_MS;
    if (age < 0 || age > 1) {
      continue;
    }
    const { x, groundY, height } = ballAt(shot, at);
    const spread = BALL_RADIUS * (0.8 + 1.9 * age);
    const alpha = 0.72 * Math.pow(1 - age, 1.5);
    const tint = SMOKE.map((c, k) => Math.round(lerp(c, SMOKE_OLD[k], age)));
    const drift = hash(shot.id, i) - 0.5;
    // Puffs sag toward the ground as they age rather than hanging at the height
    // the ball passed through.
    const px = x + drift * BALL_RADIUS;
    const py = groundY - height * (1 - age * 0.6);
    const rgb = `${tint[0]}, ${tint[1]}, ${tint[2]}`;
    const grad = ctx.createRadialGradient(px, py, 0, px, py, spread);
    grad.addColorStop(0, `rgba(${rgb}, ${alpha})`);
    grad.addColorStop(0.5, `rgba(${rgb}, ${alpha * 0.7})`);
    grad.addColorStop(1, `rgba(${rgb}, 0)`);
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.ellipse(px, py, spread, spread * 0.8, 0, 0, 2 * Math.PI);
    ctx.fill();
  }
  ctx.restore();
};

export const drawCannonball = (ctx, shot, elapsedMs) => {
  const t = elapsedMs / shot.flight_ms;
  if (t < 0 || t > 1) {
    return;
  }
  const { x, groundY, height, peak } = ballAt(shot, t);

  // The shadow stays on the ground while the ball rides above it. That gap is
  // what actually reads as height — without it a rising ball just looks like a
  // ball moving up the screen.
  const shadowScale = 1 - 0.4 * (height / (peak || 1));
  ctx.fillStyle = `rgba(22, 26, 16, ${0.32 * shadowScale})`;
  ctx.beginPath();
  ctx.ellipse(x, groundY, BALL_RADIUS * 1.05 * shadowScale,
              BALL_RADIUS * 0.5 * shadowScale, 0, 0, 2 * Math.PI);
  ctx.fill();

  const y = groundY - height;
  // Rough cast iron, not a billiard ball: a dark body with lighter and darker
  // patches, matching the mottling on the reference ball.
  ctx.fillStyle = "rgba(58, 60, 56, 1)";
  ctx.beginPath();
  ctx.arc(x, y, BALL_RADIUS, 0, 2 * Math.PI);
  ctx.fill();

  ctx.save();
  ctx.beginPath();
  ctx.arc(x, y, BALL_RADIUS, 0, 2 * Math.PI);
  ctx.clip();
  ctx.fillStyle = "rgba(104, 106, 99, 0.85)";
  for (const [dx, dy, r] of [[-0.3, -0.34, 0.42], [0.26, -0.12, 0.24],
                             [-0.1, 0.3, 0.2]]) {
    ctx.beginPath();
    ctx.arc(x + BALL_RADIUS * dx, y + BALL_RADIUS * dy, BALL_RADIUS * r,
            0, 2 * Math.PI);
    ctx.fill();
  }
  ctx.fillStyle = "rgba(32, 33, 31, 0.8)";
  for (const [dx, dy, r] of [[0.42, 0.36, 0.34], [-0.46, 0.2, 0.22]]) {
    ctx.beginPath();
    ctx.arc(x + BALL_RADIUS * dx, y + BALL_RADIUS * dy, BALL_RADIUS * r,
            0, 2 * Math.PI);
    ctx.fill();
  }
  ctx.restore();
};

export const drawMuzzleFlash = (ctx, shot, elapsedMs) => {
  const t = elapsedMs / MUZZLE_MS;
  if (t < 0 || t > 1) {
    return;
  }
  const aim = Math.atan2(shot.to.y - shot.from.y, shot.to.x - shot.from.x);
  const reach = UNIT_DRAW_SIZE * 0.26;

  // The cloud sits a little ahead of the muzzle and billows out along the line
  // of fire rather than upward: the charge vents through the barrel, so the
  // smoke goes where the barrel points. The scale here shortens the plume
  // along that axis without narrowing it, keeping the discharge close to the
  // gun rather than trailing halfway to the target.
  drawBillow(ctx, shot.from.x + Math.cos(aim) * reach * 0.7,
             shot.from.y + Math.sin(aim) * reach * 0.5,
             (elapsedMs - SMOKE_DELAY_MS) / (MUZZLE_MS - SMOKE_DELAY_MS),
             UNIT_DRAW_SIZE * 0.85, shot.id, 0.41, aim);

  // The fireball is over in the first fifth of the discharge — two frames in
  // ten in the reference — and it is warm yellow-orange, not white.
  const fire = 1 - Math.min(1, elapsedMs / FIREBALL_MS);
  if (fire <= 0) {
    return;
  }
  // Roughly the ball's own diameter, and soft-edged. A hard orange disc twice
  // that size swallowed the ball and read as a sticker rather than a flash.
  const grown = 0.85 + 0.45 * (1 - fire);
  const size = BALL_RADIUS * 1.7 * grown;
  ctx.save();
  ctx.translate(shot.from.x, shot.from.y);
  ctx.rotate(aim);
  const puff = (cx, cy, r, rgb, peak) => {
    const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    grad.addColorStop(0, `rgba(${rgb}, ${peak * fire})`);
    grad.addColorStop(0.5, `rgba(${rgb}, ${peak * fire * 0.72})`);
    grad.addColorStop(1, `rgba(${rgb}, 0)`);
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.ellipse(cx, cy, r, r * 0.85, 0, 0, 2 * Math.PI);
    ctx.fill();
  };
  puff(size * 0.55, 0, size * 1.25, "233, 138, 52", 0.92);
  // yellow-white core, sitting high and forward inside the orange
  puff(size * 0.85, -size * 0.3, size * 0.72, "247, 230, 150", 0.95);
  ctx.restore();
};

export const drawBlast = (ctx, blast, elapsedMs) => {
  const t = elapsedMs / BLAST_MS;
  if (t < 0 || t > 1) {
    return;
  }
  // Same billow as the muzzle, at the same half height and at half the size
  // again — a shell landing kicks up a burst around the point of impact, not a
  // cloud the size of the units either side of it.
  drawBillow(ctx, blast.x, blast.y - UNIT_DRAW_SIZE * 0.03, t,
             UNIT_DRAW_SIZE * 0.46, `${blast.x},${blast.y}`, 0.5);

  // No scorch mark. There is none in the reference — an impact there is smoke
  // and nothing else — and the dark ellipse read as a hole in the ground with
  // a cloud sitting over it.
};

// Attack-order feedback. The move marker is already red, so the two orders are
// told apart by shape rather than colour: a crosshair that spins down onto the
// target instead of brackets closing on it.
export const RETICLE_MS = 700;

export const drawAttackReticle = (ctx, x, y, elapsedMs) => {
  const t = elapsedMs / RETICLE_MS;
  if (t < 0 || t > 1) {
    return;
  }
  const eased = 1 - Math.pow(1 - t, 3);
  const radius = 30 - 13 * eased;
  const alpha = 1 - t * t;
  const spin = eased * 0.9;

  ctx.save();
  ctx.translate(x, y);
  // flattened, so the reticle lies on the ground with everything else
  ctx.scale(1, 0.62);
  ctx.rotate(spin);
  ctx.lineCap = "round";
  ctx.lineWidth = 2.5;
  ctx.strokeStyle = `rgba(255, 196, 74, ${alpha})`;
  ctx.beginPath();
  ctx.arc(0, 0, radius, 0, 2 * Math.PI);
  ctx.stroke();
  // four ticks reaching in toward the centre, stopping short of it
  for (let i = 0; i < 4; i++) {
    const angle = (i * Math.PI) / 2;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    ctx.beginPath();
    ctx.moveTo(cos * radius * 1.35, sin * radius * 1.35);
    ctx.lineTo(cos * radius * 0.45, sin * radius * 0.45);
    ctx.stroke();
  }
  ctx.restore();
};
